import { URL } from 'url';
import qs from 'query-string-for-all';
import {
  ProductCollectionFilter,
  ProductCollectionResponse,
  ProductCollectionSliceProducts,
} from './types';

export * from './types';

const EXCLUDED_PARAMS = [
  'offset',
  'sort',
  'direction',
  'size',
  'q',
  'utm_source',
  'utm_content',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'ad',
];

const PREFIXES = {
  rangeGroup: 'rg_',
  range: 'r_',
  boolean: 'b_',
};

function unsupportedFilterType<T>(_filter: never, returnValue: T): T {
  return returnValue;
}

export const filterToQueryParams = (filter: ProductCollectionFilter) => {
  switch (filter.type) {
    case 'term': {
      return { [filter.property]: filter.id };
    }
    case 'terms': {
      return { [filter.property]: filter.ids.join('|') };
    }
    case 'range': {
      return {
        [`${PREFIXES.range}${filter.property}`]: `${filter.from}-${filter.to}`,
      };
    }
    case 'range-group': {
      return {
        [`${PREFIXES.rangeGroup}${filter.property}`]: filter.key,
      };
    }
    case 'boolean': {
      const trueValues = ['1', 'true', 'yes', 1, true];
      const value = trueValues.includes(filter.id) ? '1' : '0';
      return {
        [`${PREFIXES.boolean}${filter.property}`]: value,
      };
    }
    default:
      return unsupportedFilterType(filter, {});
  }
};

export const getSortedQuery = (query: object) =>
  Object.keys(query)
    .sort()
    .reduce(
      (result, key) => ({
        ...result,
        [key]: query[key],
      }),
      {}
    );

export const addFilterToUrl = (
  baseUrl: string,
  filter: ProductCollectionFilter
) => {
  const [baseUrlPathName, baseUrlQueryString] = baseUrl.split('?', 2);
  const baseUrlQuery = qs.parse(baseUrlQueryString || '');

  const newQuery = {
    ...baseUrlQuery,
    ...filterToQueryParams(filter),
  };

  if (Object.keys(newQuery).length === 0) {
    return baseUrlPathName;
  }

  return `${baseUrlPathName}?${qs.stringify(getSortedQuery(newQuery))}`;
};

export const addFilterToUrlBuilder = (
  urlBuilder: URL,
  filter: ProductCollectionFilter
) => {
  const filterQueryParams = filterToQueryParams(filter);
  Object.entries(filterQueryParams).forEach(([key, value]: [string, string]) =>
    urlBuilder.searchParams.set(key, value)
  );
  urlBuilder.searchParams.sort();
};

export const extractFiltersFromUrl = (url: string) => {
  const { searchParams } = new URL(url, 'http://dummy');

  const params = [...searchParams.keys()].filter(
    key => !EXCLUDED_PARAMS.includes(key)
  );

  return params
    .map(param => {
      const paramValue = searchParams.get(param);

      if (paramValue === null) {
        return null;
      }

      if (param.startsWith(PREFIXES.rangeGroup)) {
        return {
          type: 'range-group',
          property: param.substr(PREFIXES.rangeGroup.length),
          key: paramValue,
        };
      }

      if (param.startsWith(PREFIXES.range)) {
        const [fromRaw, toRaw] = paramValue.split('-');
        const from = Number.parseFloat(fromRaw);
        const to = Number.parseFloat(toRaw);

        return {
          type: 'range',
          property: param.substr(PREFIXES.range.length),
          from: !Number.isNaN(from) ? from : null,
          to: !Number.isNaN(to) ? to : null,
        };
      }

      if (param.startsWith(PREFIXES.boolean)) {
        return {
          type: 'boolean',
          property: param.substr(PREFIXES.boolean.length),
          id: ['1', 'true', 'yes'].includes(paramValue),
        };
      }

      // Values can be divided by | or , which indicates OR AND operators
      const delimiter = paramValue.includes(',') ? ',' : '|';
      const operator = delimiter === ',' ? 'AND' : 'OR';
      const values = paramValue.split(delimiter);

      return values.length === 1
        ? {
            type: 'term',
            property: param,
            id: values[0],
          }
        : {
            type: 'terms',
            property: param,
            ids: values,
            operator,
          };
    })
    .filter(Boolean);
};

const getProductsSlice = (data: ProductCollectionResponse) =>
  data.slices.find(
    (slice): slice is ProductCollectionSliceProducts =>
      slice.type === 'products'
  );

const addOffsetToUrlBuilder = (
  urlBuilder: URL,
  data: ProductCollectionResponse
) => {
  const productSlice = getProductsSlice(data);
  if (productSlice && productSlice.offset > 0) {
    urlBuilder.searchParams.set('offset', `${productSlice.offset}`);
    urlBuilder.searchParams.sort();
  }
  return urlBuilder;
};

type URLOptions = {
  includeOffset?: boolean;
};

export const getUrlFromResponse = (
  data: ProductCollectionResponse,
  baseUrl: string,
  options: URLOptions = { includeOffset: true }
) => {
  const urlBuilder = new URL(data.baseUrl, baseUrl);
  data.filters.forEach(filter => addFilterToUrlBuilder(urlBuilder, filter));
  if (options.includeOffset) {
    addOffsetToUrlBuilder(urlBuilder, data);
  }
  return urlBuilder;
};

export const getCanonicalUrlFromResponse = (
  data: ProductCollectionResponse,
  baseUrl: string,
  options: URLOptions = { includeOffset: true }
) => {
  const urlBuilder = new URL(data.baseUrl, baseUrl);
  data.navigationState.previous.forEach(({ filters }) =>
    filters.forEach(f => addFilterToUrlBuilder(urlBuilder, f))
  );
  if (options.includeOffset) {
    addOffsetToUrlBuilder(urlBuilder, data);
  }
  return urlBuilder;
};

export const getNextUrlFromResponse = (
  data: ProductCollectionResponse,
  baseUrl: string
) => {
  const productSlice = getProductsSlice(data);
  if (
    !productSlice ||
    productSlice.pagination.currentPage >= productSlice.pagination.pages
  ) {
    return null;
  }
  const { pagination, size } = productSlice;
  const urlBuilder = getCanonicalUrlFromResponse(data, baseUrl, {
    includeOffset: false,
  });
  urlBuilder.searchParams.set('offset', `${pagination.currentPage * size}`);
  urlBuilder.searchParams.sort();
  return urlBuilder;
};

export const getPreviousUrlFromResponse = (
  data: ProductCollectionResponse,
  baseUrl: string
) => {
  const productSlice = getProductsSlice(data);
  if (!productSlice || productSlice.pagination.currentPage === 1) {
    return null;
  }
  const { offset, size } = productSlice;
  const urlBuilder = getCanonicalUrlFromResponse(data, baseUrl, {
    includeOffset: false,
  });
  const newOffset = offset - size;
  if (newOffset > 0) {
    urlBuilder.searchParams.set('offset', `${newOffset}`);
    urlBuilder.searchParams.sort();
  }
  return urlBuilder;
};
